import { batch } from 'react-redux'
import { format } from 'date-fns'

import { makeActionCreator } from '../../config/store/utils'
import { PURCHASE_ORDER_VALID_DICTIONARY } from '../../config/settings/constants'
import { formatTypeCurrency } from '../../modules/utils/formatTypeCurrency'

import { getCompany, getQuoteSummary } from '../../services/quoteSummary'

export const GET_QUOTE_BASIC_INFO = 'GET_QUOTE_BASIC_INFO'
export const GET_QUOTE_BASIC_INFO_ERROR = 'GET_QUOTE_BASIC_INFO_ERROR'
export const GET_QUOTE_BASIC_INFO_SUCCESS = 'GET_QUOTE_BASIC_INFO_SUCCESS'
export const onGetQuote = makeActionCreator(GET_QUOTE_BASIC_INFO)
export const onGetQuoteError = makeActionCreator(GET_QUOTE_BASIC_INFO_ERROR, 'payload')
export const onGetQuoteSuccess = makeActionCreator(GET_QUOTE_BASIC_INFO_SUCCESS, 'payload')
export const onGetQuoteSummaryThunk =
  ({ quoteId, pageId }) =>
  async dispatch => {
    dispatch(onGetQuote())
    try {
      if (pageId === undefined) dispatch(onPage(0))
      const nextPage = pageId > 0 ? pageId - 1 : pageId
      const { data } = await getQuoteSummary({ quoteId, pageId: nextPage })
      const openingDate = new Date(data.payload.detalleSolicitud.fechaApertura)
      const closingDate = new Date(data.payload.detalleSolicitud.fechaCierre)
      const secondCallDate = new Date()
      const differenceInMilliseconds = closingDate.getTime() - openingDate.getTime()
      const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24)
      secondCallDate.setDate(closingDate.getDate() + differenceInDays)

      const purcharseOrderStatus = {
        allowChange: data?.payload?.alertasOc?.alertaOcDesactivable,
        statusRemoved: data?.payload?.alertasOc?.alertaOcEliminada,
        statusCancelled: data?.payload?.alertasOc?.alertaOcCancelada
      }

      const basicInfo = {
        id: data.payload.detalleSolicitud.id,
        name: data.payload.detalleSolicitud.nombre,
        description: data.payload.detalleSolicitud.descripcion,
        address: data.payload.detalleSolicitud.direccion,
        secondCallDate: format(new Date(secondCallDate), "dd-MM-yyyy 'a las' HH:mm:ss"),
        datePublic: data.payload.detalleSolicitud.fechaPublicacion
          ? format(new Date(data.payload.detalleSolicitud.fechaPublicacion), "dd-MM-yyyy 'a las' HH:mm:ss")
          : '',
        dateClose: format(new Date(data.payload.detalleSolicitud.fechaCierre), "dd-MM-yyyy 'a las' HH:mm:ss"),
        contactName: data.payload.detalleSolicitud.nombreContacto,
        phones: data.payload.detalleSolicitud.telefono,
        email: data.payload.detalleSolicitud.email,
        quoteQuantity: data.payload.cantidadCotizaciones,
        state: data.payload.detalleSolicitud.estado,
        statusId: data.payload.detalleSolicitud.idEstado,
        reasonCancellation: data.payload.detalleSolicitud.motivoCancelacion,
        currency: data.payload.detalleSolicitud.moneda,
        amount: data.payload.detalleSolicitud.montoMoneda.replaceAll(',', ''),
        totalEstimatedAmount: `$ ${data.payload.detalleSolicitud.montoTotalEstimado}`,
        deliveryTime: `${data.payload.detalleSolicitud.plazoEntrega} días`,
        notifiedSellers: !data.payload.proveedoresNotificados ? 0 : parseInt(data.payload.proveedoresNotificados, 10),
        finesPenalties: !!data.payload.detalleSolicitud.esMultaSancion,
        causes: data.payload.causas,
        budgetType: data.payload.tipoPresupuesto,
        reasonDeserted: data.payload.motivoDesierta,
        cancellationType: data?.payload?.detalleSolicitud?.tipoCancelacion || null,
        isEnvironmental: data?.payload?.consideraRequisitosMedioambientales,
        isSocialEconomic: data?.payload?.consideraRequisitosImpactoSocialEconomico
      }
      const statusCallAgile = {
        isSecondCall: data.payload?.estadoConvocatoriaAgil.convocatoria.includes('Segundo'),
        call: data.payload?.estadoConvocatoriaAgil.convocatoria,
        secondCallAutomatic:
          data.payload?.estadoConvocatoriaAgil.segundoAutomatico === 'No Activo' &&
          data.payload?.estadoConvocatoriaAgil.segundoManual === 'No Activo'
            ? 'Esta Compra Ágil no considera un segundo llamado automático'
            : format(
                new Date(data.payload?.estadoConvocatoriaAgil.fechaCierreSegundoLlamado),
                "dd-MM-yyyy 'a las' HH:mm:ss"
              ),
        secondCallManual: data.payload?.estadoConvocatoriaAgil.segundoAutomatico,
        dateCloseFirstCall: format(
          new Date(data.payload?.estadoConvocatoriaAgil.fechaCierrePrimerLlamado),
          "dd-MM-yyyy 'a las' HH:mm:ss"
        )
      }
      const paginationMeta = {
        totalItems: data.payload.totalItems,
        totalPages: data.payload.totalPages,
        currentPage: pageId
      }

      const quoteProducts = data.payload.detalleSolicitud.productos.map(product => ({
        id: product.id,
        description: product.descripcion,
        name: product.nombre,
        productCode: product.codigoProducto,
        quantity: product.cantidad,
        unitMeasure: product.unidad.nombre === null ? product.unidad.codigo : product.unidad.nombre,
        subtotal: 0,
        unitValue: 0,
        included: true
      }))

      const institution = {
        company: data.payload.detalleSolicitud.institucion.empresa,
        organization: data.payload.detalleSolicitud.institucion.organizacion,
        dni: data.payload.detalleSolicitud.institucion.rut
      }
      const countInadmissible = data.payload.ofertasInadmisibles

      const countPurchaseCanceled = data?.payload?.countCotCanceladas

      const biddingDraf =
        data.payload.ofertas !== null &&
        data.payload.ofertas.map(bid => {
          return {
            dni: bid.rut,
            name: bid.nombre,
            businessName: bid.razonSocial,
            companySize: bid.tamanoEmpresa,
            amount: bid.montoTotal,
            description: bid.descripcion,
            bidId: bid.id,
            isSelected: bid.isSeleccionable,
            purchaseOrderStatus:
              bid.ordenesCompra &&
              bid.ordenesCompra.map(po => ({
                purchaseOrderId: po.id,
                purchaseOrderCode: po.code,
                purchaseOrderEstatusCode: po.idEstado,
                isActive: po.activo === PURCHASE_ORDER_VALID_DICTIONARY.ACTIVE
              })),
            typeOffer: bid.tipoOferta,
            validityDate: format(new Date(bid.fechaVigencia), 'dd-MM-yyyy'),
            order: bid.orden,
            statusBidId: bid.estadoId,
            region: bid.regiones && bid.regiones[0] ? bid.regiones[0] : 'sin region',
            inadmissibility: bid.justificacion,
            offeredProducts: (bid.productosOfertados || []).map(productoOfertado => ({
              quoteRequestProductId: productoOfertado.solicitudCotizacionProductoId,
              productCode: productoOfertado.codigoProducto,
              quantity: productoOfertado.cantidad,
              unitPrice: productoOfertado.precioUnitario,
              totalAmount: productoOfertado.montoTotal,
              unity: productoOfertado.unidad,
              description: productoOfertado.descripcion,
              name: productoOfertado.nombre,
              include: productoOfertado.incluir
            })),
            purchaseOrder: (bid.ordenesCompra || []).map(oc => ({
              amount: oc.monto
                ? formatTypeCurrency(data.payload.detalleSolicitud.moneda, oc.monto.toString().replace(/,/g, ''), true)
                : null,
              code: oc.code,
              date: oc.fechaCreacion,
              glossState: oc.estadoGlosa,
              id: oc.id,
              idState: oc.idEstado,
              isActive: oc.activo === PURCHASE_ORDER_VALID_DICTIONARY.ACTIVE,
              messageStatusSaved: oc.msgEstadoGuardada,
              supplierDni: oc.rutProveedor,
              supplierLegalName: oc.razonSocialProveedor,
              text: oc.estadoGlosaFront
            }))
          }
        })
      const bidding = biddingDraf && biddingDraf?.reduce((dictionary, bid) => ({ ...dictionary, [bid.bidId]: bid }), {})
      const selectedProvidersDraf =
        data.payload.ofertasSeleccionadas != null &&
        data.payload.ofertasSeleccionadas.map(selectedProvider => ({
          bidId: selectedProvider.id,
          name: selectedProvider.nombre,
          businessName: selectedProvider.razonSocial,
          companySize: selectedProvider.tamanoEmpresa,
          description: selectedProvider.descripcion,
          companyCode: selectedProvider.codigoEmpresa,
          branchCode: selectedProvider.codigoSucursalEmpresa,
          totalAmount: selectedProvider.montoTotal,
          isSelectedOffer: selectedProvider.esOfertaSeleccionada,
          isSwornDeclaration: selectedProvider.esDeclaracionJurada,
          validityDate: format(new Date(selectedProvider.fechaVigencia), 'dd-MM-yyyy'),
          isPurchaseOrder: selectedProvider.isOrdenCompra,
          selectionCriteria: selectedProvider.criterioSeleccion,
          dni: selectedProvider.rut,
          purchaseOrder: selectedProvider.ordenesCompra?.map(oc => ({
            amount: oc.monto
              ? formatTypeCurrency(data.payload.detalleSolicitud.moneda, oc.monto.toString().replace(/,/g, ''), true)
              : null,
            code: oc.code,
            date: oc.fechaCreacion,
            glossState: oc.estadoGlosa,
            id: oc.id,
            idState: oc.idEstado,
            isActive: oc.activo === PURCHASE_ORDER_VALID_DICTIONARY.ACTIVE,
            messageStatusSaved: oc.msgEstadoGuardada,
            supplierDni: oc.rutProveedor,
            supplierLegalName: oc.razonSocialProveedor,
            text: oc.estadoGlosaFront
          })),
          typeOffer: selectedProvider.tipoOferta
        }))

      const selectedProviders =
        selectedProvidersDraf &&
        selectedProvidersDraf?.reduce((diccionary, bid) => ({ ...diccionary, [bid.bidId]: bid }), {})

      const bidsSeller = data?.payload?.detalleOfertasProveedor?.map(bidSeller => ({
        amount: bidSeller.montoTotal,
        attachedFiles: bidSeller.documentosAdjuntos.map(attachedFile => ({
          id: attachedFile.id,
          name: attachedFile.filename
        })),
        detail: bidSeller.detalle,
        dispatchValue: bidSeller.valorDespacho,
        effectiveDate: bidSeller.fechaVigencia,
        id: bidSeller.id,
        nethworth: bidSeller.valorNeto,
        products: bidSeller.prCotizacion.map(product => ({
          amount: product.montoTotal,
          description: product.descripcion,
          id: product.solicitudCotizacionProductoId,
          name: product.nombre,
          quantity: product.cantidad,
          unit: product.unidad,
          unitPrice: product.precioUnitario,
          productCode: product.codigoProducto
        })),
        sendDate: bidSeller.fechaEnvio,
        taxation: bidSeller.impuesto,
        taxationValue: bidSeller.valorImpuesto
      }))

      return dispatch(
        onGetQuoteSuccess({
          basicInfo,
          bidding,
          bidsSeller,
          countInadmissible,
          countPurchaseCanceled,
          institution,
          paginationMeta,
          purcharseOrderStatus,
          quoteProducts,
          selectedProviders,
          statusCallAgile
        })
      )
    } catch (error) {
      if (error.code === 400 || error.code === 403) {
        const err = error.originalError.response.data.errores.map(error => ({
          code: error.codigo,
          description: error.descripcion
        }))

        return dispatch(onGetQuoteError({ error: err }))
      }
      return dispatch(onGetQuoteError({ error: {} }))
    }
  }

export const TOGGLE_DIALOG_ALERT = 'TOGGLE_DIALOG_ALERT'
export const onToggleDialogAlert = makeActionCreator(TOGGLE_DIALOG_ALERT, 'payload')

export const CLEAN_ERRORS_QUOTE_BASIC_INFORMATION = 'CLEAN_ERRORS_QUOTE_BASIC_INFORMATION'
export const onCleanErrorsQuoteBasicInformation = makeActionCreator(CLEAN_ERRORS_QUOTE_BASIC_INFORMATION, 'payload')

export const CLEAN_QUOTE_SUCCESS = 'CLEAN_QUOTE_SUCCESS'
export const onCleanQuoteSuccess = makeActionCreator(CLEAN_QUOTE_SUCCESS, 'payload')

export const GET_COMPANY = 'GET_COMPANY'
export const GET_COMPANY_ERROR = 'GET_COMPANY_ERROR'
export const GET_COMPANY_BIDDING_SUCCESS = 'GET_COMPANY_BIDDING_SUCCESS'
export const GET_COMPANY_SELECTED_PROVIDERS_SUCCESS = 'GET_COMPANY_SELECTED_PROVIDERS_SUCCESS'
export const onGetCompany = makeActionCreator(GET_COMPANY)
export const onGetCompanyError = makeActionCreator(GET_COMPANY_ERROR, 'payload')
export const onGetCompanyBiddingSuccess = makeActionCreator(GET_COMPANY_BIDDING_SUCCESS, 'payload')
export const onGetCompanySelectedProvidersSuccess = makeActionCreator(GET_COMPANY_SELECTED_PROVIDERS_SUCCESS, 'payload')
export const onGetCompanyThunk =
  ({ bidId }) =>
  async (dispatch, getState) => {
    dispatch(onGetCompany())

    try {
      const { data } = await getCompany({ bidId })
      const { bidding } = getState().quoteBasicInformation

      const company = {
        ...bidding,
        [bidId]: {
          ...bidding[bidId],
          dni: data?.payload?.cotizacionEmpresa?.rut,
          name: data?.payload?.cotizacionEmpresa?.nombre,
          businessName: data?.payload?.cotizacionEmpresa?.razonSocial,
          companySize: data?.payload?.cotizacionEmpresa?.tamanoEmpresa
        }
      }

      return batch(() => {
        dispatch(onGetCompanyBiddingSuccess({ company }))
        dispatch(onSetSelectedProvidersCompany({ bidId }))
      })
    } catch (error) {
      if (error.code === 400 || error.code === 403) {
        const err = error.originalError.response.data.errores.map(error => ({
          code: error.codigo,
          description: error.descripcion
        }))

        return dispatch(onGetCompanyError({ error: err }))
      }
      return dispatch(onGetCompanyError({ error: {} }))
    }
  }

export const onSetSelectedProvidersCompany =
  ({ bidId }) =>
  (dispatch, getState) => {
    try {
      const { bidding, selectedProviders } = getState().quoteBasicInformation
      if (Object.values(selectedProviders).some(selected => selected.bidId === bidId)) {
        const company = {
          ...selectedProviders,
          [bidId]: {
            ...selectedProviders[bidId],
            name: bidding[bidId].name,
            businessName: bidding[bidId].businessName,
            companySize: bidding[bidId].companySize,
            dni: bidding[bidId].dni
          }
        }
        return dispatch(onGetCompanySelectedProvidersSuccess({ company }))
      }
    } catch (error) {
      return dispatch(onGetCompanyError({ error }))
    }
  }

export const UPDATED_STATUS_QUOTE_BID = 'UPDATED_STATUS_QUOTE_BID'
export const onUpdatedStatusQuoteBid = makeActionCreator(UPDATED_STATUS_QUOTE_BID, 'payload')

export const CONTINADMISSIBLE = 'COUNTINADMISSIBLE'
export const onContInadmissible = makeActionCreator(CONTINADMISSIBLE, 'payload')

export const PAGE = 'PAGE'
export const onPage = makeActionCreator(PAGE, 'payload')
